<script setup lang="ts">
    import BaseCupLoader from '@/components/BaseCupLoader.vue';
    import { useLoadedProduct } from '@/composables/useLoadedProduct';
    import { EProductFormat } from '@/domain/product/EProductFormat';
    import { bandLayout } from '@/mocks/gobeletcupBandZone';
    import { tallZoneLayout } from '@/mocks/gobeletcupTallZone';
    import { useSceneZoneKey } from '@/composables/useSceneZoneKey';
    import { composeDevelopedSurface } from '@/utils/composeDevelopedSurface';
    import { faceOrbitAngleInRad } from '@/utils/faceOrbitAngle';
    import { throttle } from '@/utils/throttle';
    import { useOrderStore } from '@/store/order.store';
    import { useConfigStore } from '@/store/config.store';
    import { useEditor2D } from '@/composables/useEditor2D';
    import { _disposeEditor3D, useEditor3D } from '@/composables/useEditor3D';
    import { storeToRefs } from 'pinia';
    import { useElementSize } from '@vueuse/core';
    import { computed, nextTick, onMounted, onUnmounted, shallowRef, useTemplateRef, watch } from 'vue';
    import type { Preview3DController } from '@/application/Preview3DController';
    import type { TCanvasSnapshot } from '@/application/ports/TCanvasSnapshot';
    import type { TSide } from '@/domain/shared/TSide';

    const canvasRef = useTemplateRef<HTMLCanvasElement | null>('canvas');
    const containerRef = useTemplateRef<HTMLDivElement | null>('container');

    const { currentSide, isMobile, productType, zoom3dFactor } = storeToRefs(useConfigStore());
    const { quadriPlacement, reference, technique } = storeToRefs(useOrderStore());
    const product = useLoadedProduct();

    const controller = shallowRef<Preview3DController | null>(null);
    const isReady = shallowRef(false);
    const { height: containerHeight, width: containerWidth } = useElementSize(containerRef);
    let unsubscribeCallbacks: (() => void)[] = [];

    // A canvas smaller than the wall is laid onto the wall where it sits, instead of being
    // stretched round all of it: a pitcher's faces (see useProductSides), each the size of one
    // zone, and the short band of a cup that lets the customer place it (see gobeletcupBandZone),
    // at the height chosen on the 3D screen and the one its quantity gives.
    function zoneLayout() {
        const isBandProduct = product.value.height <= (reference.value?.quadriShort?.height ?? 0) + 1;
        if (technique.value === 'quadriShort' && isBandProduct) {
            return bandLayout(reference.value, quadriPlacement.value);
        }
        return technique.value === 'quadriTall' ? tallZoneLayout(reference.value, product.value.height) : undefined;
    }

    const faceLayout = computed(() => {
        const pitcher = reference.value;
        if (pitcher?.hasVerso && Math.abs(product.value.width - pitcher.serigraphy.width) <= 1) {
            const { marginSide, marginTop, width } = pitcher.serigraphy;
            return {
                isPitcher: true,
                surface: pitcher.surface,
                top: marginTop,
                // The verso is half a turn from the recto: the same margin on each side of a zone.
                zone: pitcher.serigraphy,
                zoneLeftInMm: { recto: marginSide, verso: marginSide + width + marginSide },
            };
        }
        // The product carries the zone it is cut to (the band, or the tall zone the quantity and
        // Express give); a product still the whole wall (the style has just changed) is not a zone yet.
        const band = zoneLayout();
        if (!band) {
            return undefined;
        }
        return {
            isPitcher: false,
            surface: band.surface,
            top: band.topInMm,
            zone: band.zone,
            zoneLeftInMm: { recto: band.leftInMm, verso: band.leftInMm },
        };
    });

    let unsubscribeTextures: (() => void)[] = [];
    const latestFaces: Partial<Record<TSide, TCanvasSnapshot>> = {};

    // The band moves on the wall as the placement changes: the last snapshot is laid again.
    watch(
        () => faceLayout.value?.top,
        () => {
            const latest = latestFaces.recto;
            if (controller.value && latest) {
                pushTexture(controller.value, 'recto', latest);
            }
        },
    );

    function pushTexture(preview: Preview3DController, side: TSide, snapshot: TCanvasSnapshot) {
        const layout = faceLayout.value;
        if (!layout) {
            preview.applyCanvasTexture(side, snapshot);
            return;
        }
        latestFaces[side] = snapshot;
        const faces = (['recto', 'verso'] as const).flatMap((faceSide) => {
            const face = latestFaces[faceSide];
            return face ? [{ canvas: face.canvas, leftInMm: layout.zoneLeftInMm[faceSide] }] : [];
        });
        // Transparent between the zones, so the glass shows through.
        preview.applyCanvasTexture('recto', {
            canvas: composeDevelopedSurface({ faces, surfaceInMm: layout.surface, topInMm: layout.top, zoneInMm: layout.zone }),
            hasOpaqueBackground: false,
        });
    }

    function wireTexturePush(preview: Preview3DController) {
        unsubscribeTextures.forEach((unsubscribe) => unsubscribe());
        unsubscribeTextures = [];
        const sides: TSide[] = product.value.format === EProductFormat.RECTO_VERSO ? ['recto', 'verso'] : ['recto'];
        if (!sides.includes('verso')) {
            delete latestFaces.verso;
        }
        sides.forEach((side) => {
            const editor2D = useEditor2D(side);
            if (!editor2D) {
                return;
            }

            const push = throttle(
                async () => {
                    const snapshot = await editor2D.exports.exportPreviewZoneCanvas();
                    pushTexture(preview, side, snapshot);
                },
                150,
                { throttleTimer: false },
            );

            push();
            unsubscribeTextures.push(editor2D.onDidChange(() => push()));
        });
    }

    // The second face appears, or goes, while the preview is up: its canvas is mounted by then,
    // not before.
    watch(
        () => product.value.format,
        async () => {
            await nextTick();
            if (controller.value) {
                wireTexturePush(controller.value);
            }
        },
    );

    // The canvases are rebuilt when the product is cut to another zone (the shells key them on it),
    // and the textures must come from the new ones.
    const sceneZoneKey = useSceneZoneKey();
    watch(sceneZoneKey, async () => {
        await nextTick();
        if (controller.value) {
            wireTexturePush(controller.value);
        }
    });

    // The pitcher turns to the face being worked on, recto or verso.
    watch(currentSide, (side) => {
        void turnToFace(side, true);
    });

    function turnToFace(side: TSide, isAnimated: boolean) {
        const layout = faceLayout.value;
        if (!layout?.isPitcher || !controller.value) {
            return Promise.resolve();
        }
        const centreInMm = layout.zoneLeftInMm[side] + layout.zone.width / 2;
        return controller.value.camera.turnTo(faceOrbitAngleInRad(centreInMm / layout.surface.width), isAnimated ? undefined : 0);
    }

    onMounted(async () => {
        if (!canvasRef.value || !containerRef.value) {
            return;
        }
        const binding = useEditor3D(canvasRef.value, {
            container: containerRef.value,
            isMobile: isMobile.value,
            urlProductType: productType.value,
        });
        if (!binding) {
            return;
        }
        controller.value = binding.controller;
        await binding.ready;
        isReady.value = binding.isReady.value;
        wireTexturePush(binding.controller);
        await turnToFace(currentSide.value, false);

        // Only now does the strategy root exist, so the framing distance is
        // known and the wheel can be bounded to the same range as the buttons.
        // Subscribed here rather than from the zoom UI: those components mount
        // before this one creates the editor, so they would silently subscribe
        // to nothing.
        binding.controller.camera.syncZoomBounds();
        zoom3dFactor.value = binding.controller.camera.getZoomFactor();
        unsubscribeCallbacks.push(
            binding.controller.camera.onCameraMoved(() => {
                if (binding.controller.camera.isFraming) {
                    return;
                }
                zoom3dFactor.value = binding.controller.camera.getZoomFactor();
            }),
        );
    });

    watch([containerWidth, containerHeight], () => {
        controller.value?.resize();
    });

    onUnmounted(() => {
        unsubscribeCallbacks.forEach((u) => u());
        unsubscribeCallbacks = [];
        unsubscribeTextures.forEach((unsubscribe) => unsubscribe());
        unsubscribeTextures = [];
        _disposeEditor3D(canvasRef.value);
        controller.value = null;
    });

    defineExpose({ controller });
</script>

<template>
    <div
        ref="container"
        class="relative h-full w-full"
    >
        <!-- data-cup-view: where the cart's picture of the configured cup is read from
             (useCupViewCapture) -->
        <canvas
            ref="canvas"
            data-cup-view
            data-test="webgl-canvas"
            :class="['h-full w-full transition-opacity duration-200', !isReady && 'opacity-0']"
        />
        <div
            v-if="!isReady"
            class="absolute inset-0 grid place-items-center"
            data-test="webgl-loader"
        >
            <BaseCupLoader />
        </div>
        <slot :controller />
    </div>
</template>
